import type { KeyboardEventHandler } from 'react'
import { SearchIcon } from './Icons'
import { X } from 'lucide-react'

interface SearchInputProps {
  placeholder?: string
  className?: string
  inputClassName?: string
  variant?: 'default' | 'boxed' | 'header' | 'filled'
  value?: string
  onChange?: (value: string) => void
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  onFocus?: () => void
  onClear?: () => void
  inputRef?: React.Ref<HTMLInputElement>
}

function cleanInputClassName(className: string): string {
  return className || ''
}

export function SearchInput({
  placeholder = 'Search',
  className = '',
  inputClassName = '',
  variant = 'default',
  value,
  onChange,
  onKeyDown,
  onFocus,
  onClear,
  inputRef,
}: SearchInputProps) {
  // Preserve responsiveness for header variant, enforce max-w-[301px] default only for standard inputs
  const labelClassName =
    variant === 'header'
      ? (className || 'w-full')
      : className
        ? (className.includes('w-') && !className.includes('max-w-') && !className.includes('w-[')
            ? `${className} max-w-[301px]`
            : className)
        : 'w-full max-w-[301px]'

  const cleanedInputClassName = cleanInputClassName(inputClassName)
  const placeholderClass = 'placeholder:text-slate-400 placeholder:text-[13px]'
  const iconColor = variant === 'header' ? 'text-[#111111]' : 'text-[#8894A2]'
  const iconPositionClass = variant === 'header' ? 'left-[16px] top-[12px]' : 'left-[14px] top-1/2 -translate-y-1/2'
  const cancelBtnHideClass = '[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden'

  const baseInputClassName =
    variant === 'filled'
      ? `h-[44px] w-full rounded-[10px] border border-transparent bg-[#f3f4f6] pl-[45px] pr-10 text-[14px] text-slate-700 outline-none transition ${placeholderClass} placeholder:font-normal placeholder:font-sans placeholder:leading-[20px] hover:bg-[#eef0f4] focus:bg-white focus:border-[#d1d5db] focus:ring-2 focus:ring-slate-200 ${cancelBtnHideClass}`
      : variant === 'header'
        ? `h-[44px] w-full rounded-[7px] border-0 bg-[#f3f0f0] pl-[44px] pr-10 text-[15px] text-[#111111] outline-none transition placeholder:text-[#111111] placeholder:font-normal placeholder:font-sans placeholder:leading-[20px] focus:bg-[#f3f0f0] focus:outline-none focus:ring-0 ${cancelBtnHideClass}`
        : variant === 'boxed'
          ? `h-[44px] w-full rounded-[8px] border border-[#dde6f1] bg-white pl-[45px] pr-10 text-[14px] text-slate-700 shadow-[0_4px_16px_rgba(15,23,42,0.04)] outline-none transition ${placeholderClass} placeholder:font-normal placeholder:font-sans placeholder:leading-[20px] hover:border-[#cfdae7] hover:bg-white focus:border-[#c7d4e4] focus:ring-4 focus:ring-[#dfe8f3] ${cancelBtnHideClass}`
          : `h-[44px] w-full rounded-[8px] border border-[#d1d5db] bg-white pl-[40px] pr-10 text-[14px] text-slate-700 outline-none transition ${placeholderClass} placeholder:font-normal placeholder:font-sans placeholder:leading-[20px] hover:border-[#146971] hover:bg-[#f8fbff] focus:border-[#146971] focus:ring-4 focus:ring-[#146971]/10 ${cancelBtnHideClass}`

  return (
    <label className={`relative block ${labelClassName}`}>
      <SearchIcon className={`pointer-events-none absolute h-[18px] w-[18px] ${iconPositionClass} ${iconColor}`} />
      <input
        ref={inputRef}
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        className={`${baseInputClassName} ${cleanedInputClassName}`}
      />
      {value && onClear && (
        <button
          type="button"
          onClick={onClear}
          className="absolute right-[14px] top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
          aria-label="Clear search input"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </label>
  )
}
